import { debugConfig } from './debug-config';
import { trackEnhancedApiRequest, trackEnhancedApiResponse } from './errorReporting';

interface RequestInterceptor {
  onRequest?: (config: RequestConfig) => RequestConfig | Promise<RequestConfig>;
  onResponse?: (response: Response, config: RequestConfig) => void;
  onError?: (error: any, config: RequestConfig) => void;
}

interface RequestConfig {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: any;
  component?: string;
  [key: string]: any;
}

class DebugApiClient {
  private interceptors: RequestInterceptor[] = [];

  constructor() {
    // Auto-intercept fetch if debug is enabled
    if (debugConfig.isEnabled()) {
      this.interceptFetch();
    }

    // Listen for debug config changes
    debugConfig.subscribe(() => {
      if (debugConfig.isEnabled()) {
        this.interceptFetch();
      }
    });
  }

  addInterceptor(interceptor: RequestInterceptor) {
    this.interceptors.push(interceptor);
    return () => {
      const index = this.interceptors.indexOf(interceptor);
      if (index > -1) {
        this.interceptors.splice(index, 1);
      }
    };
  }

  private interceptFetch() {
    const originalFetch = window.fetch;
    
    // Prevent double-wrapping
    if ((window.fetch as any).__debugIntercepted) {
      return;
    }

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const config: RequestConfig = this.buildRequestConfig(input, init);
      
      // Run request interceptors
      for (const interceptor of this.interceptors) {
        if (interceptor.onRequest) {
          config.url = await interceptor.onRequest(config);
        }
      }

      // Track request
      const tracking = trackEnhancedApiRequest({
        url: config.url,
        method: config.method,
        component: config.component || 'Unknown',
        headers: config.headers,
        body: config.body,
        params: this.extractUrlParams(config.url),
      });

      const startTime = typeof tracking === 'number' ? tracking : tracking.startTime;

      try {
        // Make the actual request
        const response = await originalFetch(input, init);
        
        // Clone response to read body without consuming it
        const responseClone = response.clone();
        let responseData: any;
        
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseData = await responseClone.json();
          } else if (contentType && contentType.includes('text')) {
            responseData = await responseClone.text();
          }
        } catch (e) {
          // Ignore body parsing errors
        }

        // Track response
        trackEnhancedApiResponse({
          url: config.url,
          method: config.method,
          component: config.component || 'Unknown',
          startTime: tracking,
          status: response.status,
          statusText: response.statusText,
          headers: this.headersToObject(response.headers),
          responseData,
        });

        // Run response interceptors
        for (const interceptor of this.interceptors) {
          if (interceptor.onResponse) {
            interceptor.onResponse(response, config);
          }
        }

        return response;
      } catch (error) {
        // Track error
        trackEnhancedApiResponse({
          url: config.url,
          method: config.method,
          component: config.component || 'Unknown',
          startTime: tracking,
          status: 0,
          statusText: 'Network Error',
          error,
        });

        // Run error interceptors
        for (const interceptor of this.interceptors) {
          if (interceptor.onError) {
            interceptor.onError(error, config);
          }
        }

        throw error;
      }
    };

    (window.fetch as any).__debugIntercepted = true;
  }

  private buildRequestConfig(input: RequestInfo | URL, init?: RequestInit): RequestConfig {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
    const method = init?.method || (input instanceof Request ? input.method : 'GET');
    
    const headers: Record<string, string> = {};
    if (init?.headers) {
      const h = init.headers;
      if (h instanceof Headers) {
        h.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(h)) {
        h.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, h);
      }
    }

    let body: any;
    if (init?.body) {
      if (typeof init.body === 'string') {
        try {
          body = JSON.parse(init.body);
        } catch {
          body = init.body;
        }
      } else {
        body = init.body;
      }
    }

    return {
      url,
      method,
      headers,
      body,
      component: this.guessComponentFromStack(),
    };
  }

  private headersToObject(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  private extractUrlParams(url: string): Record<string, string> | undefined {
    try {
      const urlObj = new URL(url, window.location.origin);
      const params: Record<string, string> = {};
      urlObj.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      return Object.keys(params).length > 0 ? params : undefined;
    } catch {
      return undefined;
    }
  }

  private guessComponentFromStack(): string {
    try {
      const stack = new Error().stack;
      if (!stack) return 'Unknown';
      
      const lines = stack.split('\n');
      // Look for React component names in the stack
      for (const line of lines) {
        // Skip our own functions
        if (line.includes('debug-api-client') || line.includes('errorReporting')) {
          continue;
        }
        
        // Look for component patterns
        const componentMatch = line.match(/at (\w+)\s*\(/);
        if (componentMatch && componentMatch[1]) {
          const name = componentMatch[1];
          // Check if it looks like a React component (PascalCase)
          if (name[0] === name[0].toUpperCase() && name !== 'Object') {
            return name;
          }
        }
      }
    } catch {
      // Ignore errors in stack parsing
    }
    
    return 'Unknown';
  }
}

export const debugApiClient = new DebugApiClient();

// Helper function to create a debug-aware fetch wrapper
export function createDebugFetch(componentName: string) {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const config = {
      component: componentName,
      ...init,
    };
    
    return fetch(input, config);
  };
}

// Hook for React components
export function useDebugFetch(componentName: string) {
  return createDebugFetch(componentName);
}

// Utility to add component context to existing API functions
export function withComponentContext<T extends (...args: any[]) => Promise<any>>(
  apiFunction: T,
  componentName: string
): T {
  return ((...args: Parameters<T>) => {
    // Try to inject component name into the context
    const originalFetch = window.fetch;
    window.fetch = ((input: any, init: any) => {
      return originalFetch(input, {
        ...init,
        headers: {
          ...init?.headers,
          'X-Debug-Component': componentName,
        },
      });
    }) as any;

    const result = apiFunction(...args);
    
    // Restore original fetch
    window.fetch = originalFetch;
    
    return result;
  }) as T;
}